import qs from 'qs';
import { api } from 'src/api/api';
import { StrapiResponse } from 'src/types/http';
import {
  DebentureAssetData,
  DebentureAssetRaw,
  DebentureGeneralSeriesResponse,
  DebentureLiabilityData,
  DebentureLiabilityRaw,
  DebentureLiabilitySeries,
  DebentureOperationListItem,
  DebentureSeriesGeneralData,
  DebentureServiceProviderData,
  DebenturesGeneralData,
  DebenturesGeneralResponse,
  DebenturesResumeData,
  DebenturesServiceProvidersResponse,
  DebentureSummaryResponse,
  DebentureTitleApiData,
  DebentureTitleData,
} from 'src/types/operations/debentures';

export const getDebenturesOperationsList = async ({
  page = 1,
  pageSize = 10,
  filters,
}: {
  page?: number;
  pageSize?: number;
  filters?: Record<string, any>;
}) => {
  const query = qs.stringify(
    {
      pagination: {
        page,
        pageSize,
      },
      ...(filters ? { filters } : {}),
    },
    {
      encodeValuesOnly: true,
    },
  );
  const { data } = await api.get<StrapiResponse<DebentureOperationListItem[]>>(
    `/meta-debentures-de-securitizacaos?${query}`,
  );
  return data;
};

function formatCompetencia(dataReferencia?: string): string {
  if (!dataReferencia) return '';

  const isoMatch = dataReferencia.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[2]}/${isoMatch[1]}`;
  }

  const brMatch = dataReferencia.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (brMatch) {
    return `${brMatch[2]}/${brMatch[3]}`;
  }

  return '';
}

function parseNumericString(value?: string | number | null): number | null {
  if (value === undefined || value === null || value === '' || value === '-') {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const trimmed = String(value).trim();
  if (!trimmed) return null;

  const isParenthesized = /^\(.+\)$/.test(trimmed);
  const unsigned = isParenthesized ? trimmed.slice(1, -1).trim() : trimmed;

  if (/[eE]/.test(unsigned)) {
    const sanitized = unsigned.replace(/[R$\s]/g, '').replace(/,/g, '.');
    const parsed = Number(sanitized);
    if (!Number.isFinite(parsed)) return null;
    return isParenthesized ? -Math.abs(parsed) : parsed;
  }

  const withoutCurrency = unsigned.replace(/[R$\s]/g, '');
  const normalized =
    withoutCurrency.includes(',') && withoutCurrency.includes('.')
      ? withoutCurrency.replace(/\./g, '').replace(',', '.')
      : withoutCurrency.includes(',')
        ? withoutCurrency.replace(',', '.')
        : withoutCurrency;
  const parsed = Number(normalized);

  if (Number.isNaN(parsed)) return null;
  return isParenthesized ? -Math.abs(parsed) : parsed;
}

function mapTitle(title: DebentureTitleApiData): DebentureTitleData {
  return {
    series: title.series ?? '',
    emissionDate: title.emissionDate ?? '',
    isin: title.isin ?? '',
    quantityIssued: parseNumericString(title.quantityIssued) ?? 0,
    unitValue: parseNumericString(title.unitValue) ?? 0,
    remuneration: title.remuneration ?? '',
  };
}

function mapServiceProviders(
  providers?: DebentureServiceProviderData[],
): DebenturesResumeData['serviceProviders']['bancoMandatario'] {
  return (providers ?? []).map(({ cnpj, logo, name, slug }) => ({
    cnpj: cnpj || undefined,
    logo: logo ?? undefined,
    name: name ?? '',
    slug: slug || undefined,
  }));
}

export function mapDebenturesResume(
  payload: DebentureSummaryResponse,
): DebenturesResumeData {
  return {
    general: {
      operationId: payload.general?.operationId ?? '',
      empresa: payload.general?.empresa ?? '',
      numeroEmissao: payload.general?.numeroEmissao ?? '',
      serie: payload.general?.serie ?? '',
      nomeFantasia:
        payload.general?.nomeFantasia ?? payload.general?.empresa ?? '',
      dataCompetencia: formatCompetencia(payload.general?.dataCompetencia),
    },
    serviceProviders: {
      bancoMandatario: mapServiceProviders(
        payload.serviceProviders?.bancoMandatario,
      ),
      agenteFiduciario: mapServiceProviders(
        payload.serviceProviders?.agenteFiduciario,
      ),
      instituicaoDepositaria: mapServiceProviders(
        payload.serviceProviders?.instituicaoDepositaria,
      ),
    },
    characteristics: {
      forma: payload.characteristics?.forma ?? '',
      garantiaEspecie: payload.characteristics?.garantiaEspecie ?? '',
      classe: payload.characteristics?.classe ?? '',
      situacao: payload.characteristics?.situacao ?? '',
    },
    titles: {
      seniors: (payload.titles?.seniors ?? []).map(mapTitle),
      mezzanines: (payload.titles?.mezzanines ?? []).map(mapTitle),
      subordinates: (payload.titles?.subordinates ?? []).map(mapTitle),
      uniques: (payload.titles?.uniques ?? []).map(mapTitle),
    },
  };
}

export const getDebenturesResumeData = async (
  operationId: string,
): Promise<DebenturesResumeData> => {
  const { data } = await api.get<DebentureSummaryResponse>(
    `/meta-debentures-de-securitizacao/${encodeURIComponent(
      operationId,
    )}/summary-data`,
  );

  return mapDebenturesResume(data);
};

export function mapDebenturesServiceProviders(
  payload: DebenturesServiceProvidersResponse,
): DebenturesResumeData['serviceProviders'] {
  return {
    bancoMandatario: mapServiceProviders(payload.bancoMandatario),
    agenteFiduciario: mapServiceProviders(payload.agenteFiduciario),
    instituicaoDepositaria: mapServiceProviders(payload.instituicaoDepositaria),
  };
}

export const getDebenturesServiceProviders = async (
  operationId: string,
): Promise<DebenturesResumeData['serviceProviders']> => {
  const { data } = await api.get<DebenturesServiceProvidersResponse>(
    `/meta-debentures-de-securitizacao/${encodeURIComponent(
      operationId,
    )}/service-providers`,
  );

  return mapDebenturesServiceProviders(data);
};

function normalizeText(value?: string | null): string | null {
  if (value === undefined || value === null) return null;
  const trimmed = String(value).trim();
  if (!trimmed || trimmed === '-') return null;
  return trimmed;
}

function mapSeries(
  serie: DebentureGeneralSeriesResponse,
): DebentureSeriesGeneralData {
  return {
    serie: normalizeText(serie.serie),
    isin: normalizeText(serie.isin),
    situacao: normalizeText(serie.situacao),
    forma: normalizeText(serie.forma),
    classe: normalizeText(serie.classe),
    dataEmissao: normalizeText(serie.dataEmissao),
    dataVencimento: normalizeText(serie.dataVencimento),
    garantiaEspecie: normalizeText(serie.garantiaEspecie),
    quantidadeEmitida: parseNumericString(serie.quantidadeEmitida),
    valorNominalEmissao: parseNumericString(serie.valorNominalEmissao),
  };
}

export function mapDebenturesGeneralData(
  payload: DebenturesGeneralResponse,
): DebenturesGeneralData {
  return {
    cnpjSecuritizadora: normalizeText(payload.general?.cnpjSecuritizadora),
    numeroEmissao: normalizeText(payload.general?.emissao),
    serie: normalizeText(payload.general?.serie),
    empresa: normalizeText(payload.general?.empresa),
    rentabilidade: payload.general?.rentabilidade ?? null,
    series: (payload.series ?? []).map(mapSeries),
  };
}

export const getDebenturesGeneralData = async (
  operationId: string,
): Promise<DebenturesGeneralData> => {
  const { data } = await api.get<DebenturesGeneralResponse>(
    `/meta-debentures-de-securitizacao/${encodeURIComponent(
      operationId,
    )}/general-data`,
  );

  return mapDebenturesGeneralData(data);
};

const ASSET_FIELDS = [
  'hashId',
  'operacaoId',
  'dataReferencia',
  'creditosVencerSemAtraso',
  'creditosVencerComAtraso',
  'dcNaoPagos',
  'reducaoValorEmissao',
  'direitosCreditorios',
  'dcAVencerAte30D',
  'dcAVencer31A60D',
  'dcAVencer61A90D',
  'dcAVencer91A120D',
  'dcAVencer121A150D',
  'dcAVencer151A180D',
  'dcAVencer181A360D',
  'dcAVencerAcima361D',
  'dcNaoPagosAte30D',
  'dcNaoPagos31A60D',
  'dcNaoPagos61A90D',
  'dcNaoPagos91A120D',
  'dcNaoPagos121A150D',
  'dcNaoPagos151A180D',
  'dcNaoPagos181A360D',
  'dcNaoPagosAcima361D',
  'prePagamentoLastro',
  'prePagamentoImpacto',
];

function mapMaturityBucket(
  label: string,
  value?: string | number | null,
): { label: string; value: number | null } {
  return { label, value: parseNumericString(value) };
}

export function mapDebenturesAsset(
  raw?: DebentureAssetRaw,
): DebentureAssetData {
  return {
    dataReferencia: normalizeText(raw?.dataReferencia),
    receivables: {
      semAtraso: parseNumericString(raw?.creditosVencerSemAtraso),
      comAtraso: parseNumericString(raw?.creditosVencerComAtraso),
      vencidosNaoPagos: parseNumericString(raw?.dcNaoPagos),
      provisoes: parseNumericString(raw?.reducaoValorEmissao),
      total: parseNumericString(raw?.direitosCreditorios),
    },
    maturity: {
      aVencer: [
        mapMaturityBucket('Até 30 dias', raw?.dcAVencerAte30D),
        mapMaturityBucket('31 a 60 dias', raw?.dcAVencer31A60D),
        mapMaturityBucket('61 a 90 dias', raw?.dcAVencer61A90D),
        mapMaturityBucket('91 a 120 dias', raw?.dcAVencer91A120D),
        mapMaturityBucket('121 a 150 dias', raw?.dcAVencer121A150D),
        mapMaturityBucket('151 a 180 dias', raw?.dcAVencer151A180D),
        mapMaturityBucket('181 a 360 dias', raw?.dcAVencer181A360D),
        mapMaturityBucket('Acima de 361 dias', raw?.dcAVencerAcima361D),
      ],
      vencidosNaoPagos: [
        mapMaturityBucket('Até 30 dias', raw?.dcNaoPagosAte30D),
        mapMaturityBucket('31 a 60 dias', raw?.dcNaoPagos31A60D),
        mapMaturityBucket('61 a 90 dias', raw?.dcNaoPagos61A90D),
        mapMaturityBucket('91 a 120 dias', raw?.dcNaoPagos91A120D),
        mapMaturityBucket('121 a 150 dias', raw?.dcNaoPagos121A150D),
        mapMaturityBucket('151 a 180 dias', raw?.dcNaoPagos151A180D),
        mapMaturityBucket('181 a 360 dias', raw?.dcNaoPagos181A360D),
        mapMaturityBucket('Acima de 361 dias', raw?.dcNaoPagosAcima361D),
      ],
    },
    prepayment: {
      montante: parseNumericString(raw?.prePagamentoLastro),
      impacto: normalizeText(raw?.prePagamentoImpacto),
    },
  };
}

/**
 * Fetches the most recent asset row for a Debêntures operation.
 *
 * Until a dedicated asset endpoint is available, this reads from the list
 * endpoint `/meta-debentures-de-securitizacaos`, filtering by `operacao_id`
 * and selecting only the asset fields via the Strapi `fields` param.
 */
export const getDebenturesAssetData = async (
  operationId: string,
): Promise<DebentureAssetData> => {
  const query = qs.stringify(
    {
      filters: { hashId: { $eq: operationId } },
      fields: ASSET_FIELDS,
      sort: ['dataReferencia:desc'],
      pagination: { pageSize: 1 },
    },
    { encodeValuesOnly: true },
  );

  const { data } = await api.get<StrapiResponse<DebentureAssetRaw[]>>(
    `/meta-debentures-de-securitizacaos?${query}`,
  );

  return mapDebenturesAsset(data?.data?.[0]);
};

const LIABILITY_FIELDS = [
  'hashId',
  'operacaoId',
  'dataReferencia',
  'passivoDerivativos',
  'passivoContratosTermo',
  'passivoFuturos',
  'passivoOpcoes',
  'passivoSwap',
  'reducaoValorEmissao',
  'outrosPassivos',
  'classe',
  'serie',
  'valorNominalEmissao',
  'quantidadeEmitida',
  'rendimentos',
  'amortizacoes',
  'taxaJuros',
  'isin',
  'codigoAtivo',
  'dataVencimento',
  'tipoOferta',
  'situacao',
  'pagamentoPeriodicidade',
] as const;

const LIABILITY_CATEGORIES: {
  label: string;
  pick: (raw: DebentureLiabilityRaw) => string | number | null | undefined;
}[] = [
  { label: 'Derivativos', pick: (r) => r.passivoDerivativos },
  { label: 'Contrato a termo', pick: (r) => r.passivoContratosTermo },
  { label: 'Futuros', pick: (r) => r.passivoFuturos },
  { label: 'Opções', pick: (r) => r.passivoOpcoes },
  { label: 'Swap', pick: (r) => r.passivoSwap },
  {
    label: 'Redução no valor da emissão (Provisão)',
    pick: (r) => r.reducaoValorEmissao,
  },
  { label: 'Outros', pick: (r) => r.outrosPassivos },
];

function mapLiabilitySeries(
  raw: DebentureLiabilityRaw,
): DebentureLiabilitySeries {
  return {
    id: `${raw.serie ?? ''}-${raw.isin ?? raw.codigoAtivo ?? ''}-${raw.dataReferencia ?? ''}`,
    classe: normalizeText(raw.classe),
    serie: normalizeText(raw.serie),
    valorNominalEmissao: parseNumericString(raw.valorNominalEmissao),
    dataReferencia: normalizeText(raw.dataReferencia),
    quantidadeEmitida: parseNumericString(raw.quantidadeEmitida),
    rendimentos: parseNumericString(raw.rendimentos),
    amortizacoes: parseNumericString(raw.amortizacoes),
    taxaJuros: normalizeText(raw.taxaJuros),
    isin: normalizeText(raw.isin),
    codigoAtivo: normalizeText(raw.codigoAtivo),
    dataVencimento: normalizeText(raw.dataVencimento),
    tipoOferta: normalizeText(raw.tipoOferta),
    situacao: normalizeText(raw.situacao),
    pagamentoPeriodicidade: normalizeText(raw.pagamentoPeriodicidade),
  };
}

function compareLiabilitySeriesRows(
  a: DebentureLiabilityRaw,
  b: DebentureLiabilityRaw,
): number {
  const classeCompare = (a.classe ?? '').localeCompare(
    b.classe ?? '',
    'pt-BR',
    {
      sensitivity: 'base',
    },
  );
  if (classeCompare !== 0) return classeCompare;

  return (a.serie ?? '').localeCompare(b.serie ?? '', 'pt-BR', {
    numeric: true,
  });
}

export function mapDebenturesLiability(
  rows: DebentureLiabilityRaw[],
): DebentureLiabilityData {
  const sorted = [...rows].sort((a, b) =>
    (b.dataReferencia ?? '').localeCompare(a.dataReferencia ?? ''),
  );
  const mostRecent = sorted[0];
  const mostRecentDate = mostRecent?.dataReferencia;
  const currentCompetenceRows = mostRecentDate
    ? sorted.filter((row) => row.dataReferencia === mostRecentDate)
    : [];

  return {
    competencia:
      formatCompetencia(mostRecent?.dataReferencia ?? undefined) || null,
    categories: LIABILITY_CATEGORIES.map(({ label, pick }) => ({
      label,
      value: mostRecent ? parseNumericString(pick(mostRecent)) : null,
    })),
    series: [...currentCompetenceRows]
      .sort(compareLiabilitySeriesRows)
      .map(mapLiabilitySeries),
  };
}

export const getDebenturesLiabilityData = async (
  operationId: string,
): Promise<DebentureLiabilityData> => {
  const query = qs.stringify(
    {
      filters: { hashId: { $eq: operationId } },
      fields: LIABILITY_FIELDS,
      sort: ['dataReferencia:desc'],
      pagination: { pageSize: 100 },
    },
    { encodeValuesOnly: true },
  );

  const { data } = await api.get<StrapiResponse<DebentureLiabilityRaw[]>>(
    `/meta-debentures-de-securitizacaos?${query}`,
  );

  return mapDebenturesLiability(data?.data ?? []);
};

export const generateCsv = async (filters?: Record<string, any>) => {
  const query = qs.stringify(
    {
      ...(filters ? { filters } : {}),
    },
    { encodeValuesOnly: true },
  );
  const { data } = await api.get(
    `/meta-debentures-de-securitizacao/generate-csv?${query}`,
  );
  return data;
};

export const getCsvStatus = async (jobId: string) => {
  const { data } = await api.get(
    `/meta-debentures-de-securitizacao/csv-status/${jobId}`,
  );
  return data;
};

export const downloadCsv = async (jobId: string) => {
  const response = await api.get(
    `/meta-debentures-de-securitizacao/download-csv/${jobId}`,
    {
      responseType: 'blob',
    },
  );
  return response;
};
